import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

const CROWN_QUICK_ADD_SURFACE_SELECTOR =
  '.crown-collection-product-card__options, .crown-home-mini-product__options, .crown-protocol-card__options, .crown-pdp-protocol-card__options, .crown-quick-add-compact-grid';

function formatCrownQuickAddPrice(value) {
  if (!value.startsWith('€')) return value;
  return `${value.replace(/^€\s*/, '')} €`;
}

function updateCrownQuickAddPrice(priceContainer, price, compareAtPrice = '') {
  const regularWrapper = priceContainer.querySelector('.price__regular');
  const saleWrapper = priceContainer.querySelector('.price__sale');
  const regularPrice = regularWrapper?.querySelector('.price') || priceContainer.querySelector('.price');
  const salePrice = saleWrapper?.querySelector('.price');
  const saleComparePrice = saleWrapper?.querySelector('.compare-at-price');
  const formattedPrice = formatCrownQuickAddPrice(price);
  const formattedCompareAtPrice = formatCrownQuickAddPrice(compareAtPrice);

  if (compareAtPrice && compareAtPrice !== price && saleWrapper && salePrice && saleComparePrice) {
    salePrice.textContent = formattedPrice;
    saleComparePrice.textContent = formattedCompareAtPrice;
    regularWrapper?.classList.add('price__hidden');
    saleWrapper.classList.remove('price__hidden');
    return;
  }

  if (!regularPrice) return;
  regularPrice.textContent = formattedPrice;
  regularWrapper?.classList.remove('price__hidden');
  saleWrapper?.classList.add('price__hidden');
}

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('[data-product-selection-scope], .shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener(ThemeEvents.variantSelected, this.updatePriceImmediately);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('[data-product-selection-scope], .shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener(ThemeEvents.variantSelected, this.updatePriceImmediately);
  }

  /** @param {CustomEvent} event */
  updatePriceImmediately = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const quickAddSurface = this.closest(CROWN_QUICK_ADD_SURFACE_SELECTOR);
    if (!quickAddSurface || event.target.closest(CROWN_QUICK_ADD_SURFACE_SELECTOR) !== quickAddSurface) return;
    if (event.target.dataset.productId !== this.dataset.productId) return;

    this.dataset.variantLoading = 'true';
    this.setAttribute('aria-busy', 'true');

    const optionValueId = event.detail?.resource?.id;
    const selectedOption = Array.from(event.target.querySelectorAll('[data-option-value-id]')).find(
      (option) => option instanceof HTMLElement && option.dataset.optionValueId === optionValueId
    );
    const price = selectedOption instanceof HTMLElement ? selectedOption.dataset.variantPrice : '';
    if (!price) return;

    const { priceContainer } = this.refs;
    if (priceContainer) {
      updateCrownQuickAddPrice(priceContainer, price, selectedOption.dataset.variantCompareAtPrice || '');
    }
  };

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    const sourceScope =
      event.target instanceof Element ? event.target.closest('[data-product-selection-scope]') : null;
    if (sourceScope && !sourceScope.contains(this)) return;

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    this.removeAttribute('data-variant-loading');
    this.removeAttribute('aria-busy');

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML.
    const newProductPrice =
      (this.dataset.blockId &&
        event.detail.data.html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`)) ||
      (this.dataset.productId &&
        event.detail.data.html.querySelector(`product-price[data-product-id="${this.dataset.productId}"]`)) ||
      (this.closest('quick-add-dialog') && event.detail.data.html.querySelector('product-price'));
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
